import React, { useState, useEffect } from "react";
import { Card, Tabs, Row, Col, InputNumber, Button, Table, message, Alert } from "antd";
import axios from "axios";

const Transport = ({ fileId, fileName, setSelectedFile, initialConfig }) => {
    const [activeTab, setActiveTab] = useState("input");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const [numSources, setNumSources] = useState(initialConfig?.numSources || 2);
    const [numDestinations, setNumDestinations] = useState(initialConfig?.numDestinations || 3);

    const [supply, setSupply] = useState(initialConfig?.supply || Array(numSources).fill(0));
    const [demand, setDemand] = useState(initialConfig?.demand || Array(numDestinations).fill(0));
    const [costs, setCosts] = useState(
        initialConfig?.costs || Array.from({ length: numSources }, () => Array(numDestinations).fill(0))
    );

    const [outputData, setOutputData] = useState(null);

    const getAuthToken = () => localStorage.getItem("access_token");

    useEffect(() => {
        if (fileId) {
            loadData(fileId);
        } else if (initialConfig) {
            setNumSources(initialConfig.numSources || numSources);
            setNumDestinations(initialConfig.numDestinations || numDestinations);
            setSupply(Array(initialConfig.numSources || numSources).fill(0));
            setDemand(Array(initialConfig.numDestinations || numDestinations).fill(0));
            setCosts(Array.from({ length: initialConfig.numSources || numSources }, () => Array(initialConfig.numDestinations || numDestinations).fill(0)));
            setIsSaved(false);
            setOutputData(null);
            setActiveTab("input");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId, initialConfig]);

    const loadData = async (id) => {
        try {
            const token = getAuthToken();
            if (!token) return message.error("Not authenticated.");
            setIsLoading(true);
            const res = await axios.get(`/api/retrieve_transport/${id}/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const match = res.data;
            if (match) {
                const input = match.input_data || {};
                const sup = input.supply || [];
                const dem = input.demand || [];
                setNumSources(sup.length || 0);
                setNumDestinations(dem.length || 0);
                setSupply(sup);
                setDemand(dem);
                setCosts(input.costs || []);
                setOutputData(match.result || match.output_data);
                setIsSaved(true);
                setActiveTab("output");
            } else {
                message.error("File not found");
            }
        } catch (err) {
            console.error(err);
            message.error("Failed to load data");
        } finally {
            setIsLoading(false);
        }
    };

    const saveData = async () => {
        try {
            const token = getAuthToken();
            if (!token) return message.error("Not authenticated.");
            setIsLoading(true);

            const payload = {
                name: fileName || "Transport Analysis",
                input_data: {
                    supply,
                    demand,
                    costs,
                },
            };

            const url = fileId ? `/api/update_transport/${fileId}/` : "/api/save_transport/";
            const method = fileId ? axios.put : axios.post;

            const res = await method(url, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            setOutputData(res.data.result || res.data.output_data);
            if (!fileId && res.data.id) {
                setSelectedFile(prev => ({ ...prev, id: res.data.id }));
            }
            setIsSaved(true);
            setActiveTab("output");
            message.success(fileId ? "Updated successfully" : "Saved successfully");
        } catch (err) {
            console.error("Save error:", err);
            message.error("Failed to save/update data");
        } finally {
            setIsLoading(false);
        }
    };

    const renderInputForm = () => {
        return (
            <Card title="Transport Module Input">
                <div style={{ overflowX: "auto" }}>
                    <table style={{ minWidth: "100%", borderCollapse: "collapse", textAlign: "center" }}>
                        <thead>
                            <tr>
                                <th style={{ border: "1px solid #ccc", padding: 8 }}>Sources \\ Destinations</th>
                                {Array.from({ length: numDestinations }).map((_, cIdx) => (
                                    <th key={cIdx} style={{ border: "1px solid #ccc", padding: 8 }}>
                                        Destination {cIdx + 1}
                                    </th>
                                ))}
                                <th style={{ border: "1px solid #ccc", padding: 8 }}>Supply</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: numSources }).map((_, rIdx) => (
                                <tr key={rIdx}>
                                    <td style={{ border: "1px solid #ccc", padding: 8, fontWeight: "bold" }}>
                                        Source {rIdx + 1}
                                    </td>
                                    {Array.from({ length: numDestinations }).map((_, cIdx) => (
                                        <td key={cIdx} style={{ border: "1px solid #ccc", padding: 8 }}>
                                            <InputNumber
                                                min={0}
                                                style={{ width: "100%" }}
                                                value={costs[rIdx]?.[cIdx]}
                                                onChange={(val) => {
                                                    const newCosts = [...costs];
                                                    newCosts[rIdx] = [...newCosts[rIdx]];
                                                    newCosts[rIdx][cIdx] = val || 0;
                                                    setCosts(newCosts);
                                                }}
                                            />
                                        </td>
                                    ))}
                                    <td style={{ border: "1px solid #ccc", padding: 8 }}>
                                        <InputNumber
                                            min={0}
                                            style={{ width: "100%" }}
                                            value={supply[rIdx]}
                                            onChange={(val) => {
                                                const newSupply = [...supply];
                                                newSupply[rIdx] = val || 0;
                                                setSupply(newSupply);
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td style={{ border: "1px solid #ccc", padding: 8, fontWeight: "bold" }}>
                                    Demand
                                </td>
                                {Array.from({ length: numDestinations }).map((_, cIdx) => (
                                    <td key={cIdx} style={{ border: "1px solid #ccc", padding: 8 }}>
                                        <InputNumber
                                            min={0}
                                            style={{ width: "100%" }}
                                            value={demand[cIdx]}
                                            onChange={(val) => {
                                                const newDemand = [...demand];
                                                newDemand[cIdx] = val || 0;
                                                setDemand(newDemand);
                                            }}
                                        />
                                    </td>
                                ))}
                                <td style={{ border: "1px solid #ccc", padding: 8 }}>
                                    {/* Empty cell for intersection of Supply and Demand */}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div style={{ marginTop: 16 }}>
                    <Button type="primary" onClick={saveData} loading={isLoading}>
                        {isSaved ? "Update Analysis" : "Save and Calculate"}
                    </Button>
                </div>
            </Card>
        );
    };

    const renderResults = () => {
        if (!outputData) return <p>No results yet.</p>;

        const columns = [
            { title: "Source", dataIndex: "source", key: "source" },
            { title: "Destination", dataIndex: "destination", key: "destination" },
            { title: "Units Allocated", dataIndex: "units", key: "units" },
        ];

        const dataSource = (outputData.allocation || []).map((item, index) => ({
            key: index,
            source: item.source,
            destination: item.destination,
            units: item.units,
        }));

        return (
            <Card title="Transport Analysis Results">
                {!outputData.balanced && (
                    <Alert
                        message="Problem is Unbalanced"
                        description="The total supply does not equal total demand. A dummy source or destination was added."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    bordered
                    style={{ marginBottom: 16 }}
                />
                <div style={{ fontSize: 16, fontWeight: "bold" }}>
                    Total Cost: {outputData.total_cost || 0}
                </div>
            </Card>
        );
    };

    return (
        <Card title={(fileName || "Transport Analysis") + " - Transport Module"}>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                destroyInactiveTabPane
                items={[
                    {
                        key: "input",
                        label: "Input Data",
                        children: renderInputForm()
                    },
                    {
                        key: "output",
                        label: "Results",
                        disabled: !isSaved && !outputData,
                        children: !outputData ? <p>No results yet. Save first.</p> : renderResults(),
                    }
                ]}
            />
        </Card>
    );
};

export default Transport;
